import React, { createContext, useContext, useState, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {}
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('light');

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#1976d2'
          },
          secondary: {
            main: '#dc004e'
          }
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              '*': {
                boxSizing: 'border-box',
              },
              html: {
                overflowX: 'hidden !important',
                width: '100%',
                maxWidth: '100vw',
              },
              body: {
                overflowX: 'hidden !important',
                width: '100%',
                maxWidth: '100vw',
                margin: 0,
                padding: 0,
              },
              '#root': {
                width: '100%',
                maxWidth: '100vw',
                overflowX: 'hidden !important',
              },
            },
          },
          MuiContainer: {
            styleOverrides: {
              root: {
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden',
                paddingLeft: '16px',
                paddingRight: '16px',
                '@media (min-width: 600px)': {
                  paddingLeft: '24px',
                  paddingRight: '24px',
                },
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                width: '100%',
                maxWidth: '100vw',
                overflowX: 'hidden',
              },
            },
          },
          MuiToolbar: {
            styleOverrides: {
              root: {
                width: '100%',
                maxWidth: '100vw',
                paddingLeft: '8px !important',
                paddingRight: '8px !important',
                '@media (min-width: 600px)': {
                  paddingLeft: '16px !important',
                  paddingRight: '16px !important',
                },
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 